<template>
    <div
        ref="root"
        class="hts-form-select"
        :class="{ 'has-loading': loading }">
        <span
            v-if="label"
            class="label">
            <span class="label-text">
                <slot name="label">
                    {{ label }}
                    <span
                        v-if="required"
                        class="text-error-500 ml-1">
                        *
                    </span>
                </slot>
            </span>
        </span>
        <div class="flex items-center">
            <div class="w-full">
                <hts-popover
                    ref="popover"
                    v-model="selectVisible"
                    :offset="offset"
                    :arrow="false"
                    :on-click-outside="onClickOutside"
                    :hide-on-click="false"
                    :append-to-body="appendToBody"
                    placement="bottom-start"
                    trigger="manual"
                    :max-width="'100%'">
                    <template #reference>
                        <div class="flex items-center">
                            <div
                                ref="containerEl"
                                class="hts-form-select__container w-full"
                                :class="{ ...cColorSize, ...cInputClass }"
                                @click="handleFocus">
                                <template v-if="multiple">
                                    <div
                                        class="hts-form-select__container-selected w-full"
                                        @click="handleClickSelectedItem">
                                        <div
                                            v-for="lb in labels"
                                            :key="lb"
                                            class="hts-form-select__container-selected-item">
                                            <span>
                                                {{ lb }}
                                            </span>
                                            <div
                                                v-if="!locked"
                                                class="inline-block align-middle hover:bg-gray-50 text-error-600 hover:text-error-400 cursor-pointer px-1 py-0"
                                                @click="handleDeleteItem($event, lb)">
                                                <hi-x-circle-icon
                                                    v-if="!locked"
                                                    class="h-5 w-5 mb-1"
                                                    aria-hidden="true" />
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <input
                                    v-else
                                    ref="input"
                                    v-model="cLabel"
                                    class="hts-input"
                                    readonly
                                    :placeholder="placeholder"
                                    :autocomplete="autocomplete"
                                    :required="cRequired"
                                    :disabled="locked"
                                    @blur="handleBlur"
                                    @input="onInput" >
                                <input
                                    v-model="cValue"
                                    :name="prop"
                                    :autocomplete="autocomplete"
                                    class="hts-input absolute bottom-0 left-0 !h-1 text-transparent select-none bg-transparent border-none"
                                    :required="cRequired" >
                                <span class="hts-form-select__loading" />
                                <div class="float-right inline-flex select-none">
                                    <span
                                        v-if="clearable && (cLabel || labels.length) && !locked && !required"
                                        class="inline-block align-middle">
                                        <hts-tooltip>
                                            <p>
                                                {{ $t('labels.clear') }}
                                            </p>
                                            <template #reference>
                                                <div
                                                    class="inline-block align-middle hover:bg-gray-50 text-error-600 hover:text-error-400 cursor-pointer px-1 py-0"
                                                    @click="handleClear">
                                                    <hi-x-circle-icon
                                                        class="h-6 w-6"
                                                        aria-hidden="true" />
                                                </div>
                                            </template>
                                        </hts-tooltip>
                                    </span>
                                    <div
                                        v-if="!locked"
                                        class="inline-block align-middle cursor-pointer">
                                        <hi-chevron-down-icon
                                            class="h-6 w-6"
                                            aria-hidden="true" />
                                    </div>
                                </div>
                            </div>

                            <span
                                v-if="hasSuffix"
                                class="suffix">
                                <hts-tooltip
                                    v-if="disabled"
                                    :content="contentTooltipDisabled"
                                    class="flex px-1 items-center">
                                    <template #reference>
                                        <hi-lock-closed-icon class="flex items-center w-5 h-5" />
                                    </template>
                                </hts-tooltip>
                                <slot name="suffix" />
                            </span>
                        </div>
                    </template>
                    <template v-if="filterable">
                        <div class="hts-form-select__container-input-search">
                            <input
                                ref="searchInput"
                                v-model="searchText"
                                :placeholder="$t('labels.enterSearchText')" >
                            <hi-x-circle-icon
                                v-if="searchText"
                                class="w-4 h-4 text-error-500 mx-1 cursor-pointer"
                                @click="clearSearch" />
                            <span
                                v-if="!selectAllDisabled && selectAllAvailable"
                                class="border-l px-1 text-main-500 cursor-pointer text-sm"
                                @click="handleSelectAll">
                                Chọn tất cả ({{ itemsSelectAllLength }})
                            </span>
                        </div>
                    </template>
                    <ul
                        v-if="!locked"
                        ref="listEl"
                        class="hts-form-select__list">
                        <template v-if="creatable">
                            <li
                                class="hts-form-select__list--item create-new"
                                @click="handleCreateClick">
                                <hi-plus-icon
                                    class="h-6 w-6 mr-1"
                                    aria-hidden="true" />
                                <span class="inline-block align-middle text-italic">
                                    {{ $t('labels.createNew') }}...
                                </span>
                            </li>
                        </template>
                        <template v-if="cItems.length">
                            <div
                                v-for="(item, index) in cItems"
                                :key="index">
                                <hts-tooltip
                                    v-if="keyTooltip"
                                    class="hts-form-select__list--item list-item"
                                    :max-width="250"
                                    placement="right">
                                    <template #reference>
                                        <slot
                                            :item="item"
                                            :index="index">
                                            <div
                                                class="hts-form-select__list--item"
                                                :class="{ active: selectedItemKeys.includes(item[keyId]), disabled: isDisabled(item) }"
                                                @click="handleSelect(item)">
                                                <slot
                                                    name="label"
                                                    :item="item"
                                                    :index="index">
                                                    <p
                                                        class="align-middle"
                                                        :class="{ 'font-bold': keySubLabel }">
                                                        {{ item[keyLabel] || $t('labels.invalidData') }}
                                                    </p>
                                                    <p
                                                        v-if="keySubLabel"
                                                        class="align-middle font-light">
                                                        {{ item[keySubLabel] || '' }}
                                                    </p>
                                                </slot>
                                            </div>
                                        </slot>
                                    </template>
                                    <span class="inline-block align-middle">
                                        {{ item[keyTooltip] || $t('labels.invalidData') }}
                                    </span>
                                </hts-tooltip>
                                <li
                                    v-else
                                    class="hts-form-select__list--item"
                                    :class="{ ...{ active: selectedItemKeys.includes(item[keyId]), disabled: isDisabled(item) }, ...(item.class || {}) }"
                                    @click="handleSelect(item)">
                                    <slot
                                        :item="item"
                                        :index="index">
                                        <p
                                            class="align-middle"
                                            :class="{ 'font-bold': keySubLabel }">
                                            {{ item[keyLabel] || $t('labels.invalidData') }}
                                        </p>
                                        <p
                                            v-if="keySubLabel"
                                            class="align-middle font-light">
                                            {{ item[keySubLabel] || '' }}
                                        </p>
                                    </slot>
                                </li>
                            </div>
                        </template>
                        <template v-else>
                            <li
                                v-if="!loading"
                                class="p-1 mb-1 rounded select-none text-gray-700">
                                {{ $t('labels.noData') }}
                            </li>
                        </template>
                        <li
                            v-show="searching || loading"
                            class="p-1 mb-1 rounded select-none text-gray-700">
                            {{ $t('labels.searching') }}
                        </li>
                    </ul>
                </hts-popover>
            </div>
            <slot name="custom" />
        </div>
        <slot name="footer" />
        <div
            v-if="errorMessage && errorMessage.value"
            class="hts-form-input__error my-2 mx-1">
            {{ errorMessage.value }}
        </div>

        <hts-modal
            :show="confirmed"
            :title="titleConfirm"
            type="confirm"
            :content="detailConfirm"
            @hide="clearConfirm"
            @confirm="fnConfirm" />
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { daisyuiColorSize } from '~/components/utils/daisyui'
import Selection from './-select/selection'
import Watch from './-select/watch'

const { $searchObjects } = useNuxtApp()
const props = defineProps({
  modelValue: {
    type: [String, Number, Array] as PropType<string | number | boolean | any[] | null | undefined>,
    default: () => []
  },
  text: {
    type: String,
    default: ''
  },
  items: {
    type: Array,
    default: () => []
  },
  keyId: {
    type: String,
    default: 'value'
  },
  keyDisabled: {
    type: String,
    default: 'disabled'
  },
  keyLabel: {
    type: String,
    default: 'label'
  },
  keySubLabel: {
    type: String,
    default: ''
  },
  keyTooltip: {
    type: String,
    default: undefined
  },
  size: String,
  color: String as PropType<string | undefined | null>,
  label: String as PropType<string | null | undefined>,
  placeholder: String as PropType<string | null | undefined>,
  prop: String,
  rules: Object,
  name: String,
  filterable: {
    type: Boolean,
    default: true
  },
  clearable: {
    type: Boolean,
    default: true
  },
  required: Boolean,
  disabled: Boolean,
  readonly: Boolean,
  autocomplete: {
    type: String,
    default: 'off'
  },
  query: Function,
  multiple: Boolean,
  appendToBody: {
    type: Boolean,
    default: true
  },
  selectedMultiple: {
    type: [Array, Object],
    default: () => []
  },
  useStrict: {
    type: Boolean,
    default: true
  },
  creatable: Boolean,
  labelOnlyShowText: {
    type: Boolean,
    default: false
  },
  paramPrepare: {
    type: [String, Number, Boolean, Object, Array],
    default: () => {}
  },
  confirmChange: {
    type: Boolean,
    default: false
  },
  titleConfirm: {
    type: String,
    default: () => $t('labels.confirm')
  },
  detailConfirm: {
    type: String,
    default: () => $t('labels.confirmChange')
  },
  isFocusGetData: {
    type: Boolean,
    default: false
  },
  selectAllDisabled: Boolean,
  selected: Array,
  limitItems: {
    type: Number,
    default: 100
  },
  contentTooltipDisabled: {
    type: String,
    default: 'Select is disabled'
  }
})
const emits = defineEmits(['update:modelValue', 'update:text', 'update:selected', 'change', 'create-new', 'focus'])

const { field, errorMessage } = Watch(props)

const {
  modelValue,
  prop,
  name,
  rules,
  readonly,
  disabled,
  required,
  items,
  keyId,
  keyLabel,
  keyTooltip,
  query,
  multiple,
  clearable,
  filterable,
  selectedMultiple,
  text,
  useStrict,
  labelOnlyShowText,
  paramPrepare,
  confirmChange,
  isFocusGetData,
  limitItems,
  keySubLabel
} = toRefs(props)

const getData = async (s: string | undefined) => {
  // chổ này bạn yêu cầu tất cả đều cho filter
  // if (!filterable.value) {
  //   return
  // }
  // if (labelOnlyShowText.value) {
  //   return
  // }

  if (typeof s === 'string') {
    if (query && typeof query.value === 'function') {
      loading.value = true
      await Promise.resolve(query.value(s))
        .then((data: any) => {
          cItems.value = data || []

          loading.value = false
        })
        .catch(() => {
          loading.value = false
        })
    } else {
      cItems.value = $searchObjects(items.value, [keyLabel.value, keySubLabel.value], s, { i: true, limit: limitItems.value }) as []
    }
  } else {
    cItems.value = $searchObjects(items.value, [keyLabel.value, keySubLabel.value], '', { i: true, limit: limitItems.value }) as []
  }
  handleDataChange()
  nextTick(() => {
    searching.value = false
  })
}
const getDataDebounce = $lodash.debounce(getData, 300)

const offset = ref([1, 10])
const loading = ref<boolean>(false)
const searching = ref<boolean>(false)
const input = templateRef<HTMLInputElement | null>('input', null)
const searchInput = templateRef<HTMLInputElement | null>('searchInput', null)
const root = templateRef<HTMLElement | null>('root', null)
const listEl = templateRef<HTMLElement | null>('listEl', null)
const cColorSize = computed(() => daisyuiColorSize('select', props))
const searchText = ref<string>('')
const popover = ref()
const cTempValue = ref<string | number | unknown[] | undefined>('')
const containerEl = templateRef<HTMLElement | null>('containerEl', null)
const cValue = computed({
  get () {
    return modelValue?.value
  },
  set (val: any) {
    cTempValue.value = val
    field.value && field.value.setValue(val)
    emits('update:modelValue', val)
    emits('change', selectedItems.value)
    nextTick(() => {
      if (multiple.value && containerEl.value) {
        const dataEL = containerEl.value.getBoundingClientRect()
        offset.value = [offset.value[0] || 1, dataEL.height - 20]
      }
    })
  }
})

const slots = useSlots()
const locked = computed(() => readonly.value || disabled.value)
const hasPrefix = computed(() => !!slots.prefix)
const hasSuffix = computed(() => !!slots.suffix || disabled.value)
const cInputClass = computed(() => {
  return {
    'rounded-r-lg rounded-l-none': hasPrefix.value && !hasSuffix.value,
    'rounded-l-lg rounded-r-none': hasSuffix.value && !hasPrefix.value,
    'rounded-none': hasPrefix.value && hasSuffix.value,
    'rounded-lg': !hasPrefix.value && !hasSuffix.value,
    disabled: locked.value,
    border: !locked.value
    // 'bg-gray-200': (readonly.value || disabled.value) &&
  }
})

const selectVisible = ref<boolean>(false)
const hide = () => {
  nextTick(() => {
    selectVisible.value = false
  })
}

const {
  cItems,
  changing,
  cLabel,
  confirmed,
  fnConfirm,
  selectedItems,
  selectedItemKeys,
  itemsSelectAllLength,
  selectAllAvailable,
  isDisabled,
  clearConfirm,
  handleSelect,
  handleSelectAll
} = Selection(props, emits, getDataDebounce, cValue, hide)

watch(
  () => selectedItems.value,
  (val: any) => {
    emits('update:selected', val)
  },
  { deep: true }
)

const cRequired = computed<boolean>(() => {
  if (Array.isArray(modelValue.value) && modelValue.value.length) {
    return false
  }
  return required.value
})
const itemsLength = computed<number>(() => cItems.value.length)
const labels = computed(() =>
  selectedItems.value.map((item: any) => {
    return item[keyLabel.value] || ''
  })
)

watch(
  () => modelValue.value,
  (val: string | number | any[] | undefined | null) => {
    if (!changing.value) {
      if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
        cLabel.value = ''
        selectedItems.value = []
      } else {
        field.value && field.value.setValue(val)
        prepareComponent()
        handleDataChange()
      }
    }
  }
)

watch(
  () => searchText.value,
  (val: string) => {
    searching.value = true
    getDataDebounce(val)
  }
)

watch(
  () => items.value,
  (val) => {
    prepareComponent()
    getData(searchText.value || undefined)
  }
)

watch(
  () => text.value,
  (val) => {
    cLabel.value = text.value || ''
  }
)

watch(
  () => selectedMultiple.value,
  (val) => {
    defineInitData()
  }
)

watch(
  () => paramPrepare.value,
  (val) => {
    getData(searchText.value || '')
    prepareComponent()
  },
  { deep: true }
)

const show = () => {
  if (disabled.value || readonly.value) {
    return
  }
  nextTick(() => {
    selectVisible.value = true
    nextTick(() => {
      searchInput.value && searchInput.value.focus()
    })
    if (multiple.value && containerEl.value) {
      const dataEL = containerEl.value.getBoundingClientRect()
      offset.value = [offset.value[0] || 1, dataEL.height - 20]
    }
  })
}

const handleFocus = (e: any) => {
  e.preventDefault()
  e.stopPropagation()
  nextTick(() => {
    show()
    if (!itemsLength.value || isFocusGetData.value) {
      getDataDebounce(searchText.value)
    }
  })
}

const handleBlur = (e: any) => {}

const onClickOutside = (i: any, e: any) => {
  i.hide()
  resetLabelValue()
}

const resetLabelValue = () => {
  nextTick(() => {
    if (multiple.value) {
      cLabel.value = ''
    } else if (!selectedItems.value.length) {
      if (!cLabel.value) {
        cLabel.value = ''
      }
    } else if (useStrict.value) {
      const firstItem: any = selectedItems.value[0] || {}
      cLabel.value = firstItem[keyLabel.value] || ''
    }
  })
}

const onInput = (e: any) => {
  if (!(Array.isArray(selectedItems.value) && selectedItems.value.length)) {
    if (!useStrict.value) {
      cValue.value = e.value
    }
  }
}

const handleClickSelect = (e: any) => {
  e.preventDefault()
  e.stopPropagation()
  if (selectVisible.value) {
    hide()
  } else {
    nextTick(() => {
      show()
      getDataDebounce('')
    })
  }
}

const clearSearch = () => {
  searchText.value = ''
}

const handleClear = (e: any) => {
  e?.preventDefault()
  e?.stopPropagation()
  nextTick(() => {
    if (!searchText.value) {
      getDataDebounce('')
    }
  })
  if (props.disabled) {
    return true
  }

  if (confirmChange.value) {
    if (!confirmed.value) {
      hide()
      confirmed.value = true
      fnConfirm.value = () => handleClear(e)
      return
    }
  }

  selectedItems.value = []
  cLabel.value = ''
  input.value && input.value.focus()
  if (multiple.value) {
    cValue.value = []
  } else {
    cValue.value = undefined
  }
  if (!selectVisible.value) {
    show()
  }
  clearConfirm()
}

const handleClickSelectedItem = (e: any) => {
  emits('focus', e)
}

const handleDeleteItem = (e: any, lb: string) => {
  if (e instanceof MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
  }
  nextTick(() => {
    if (!searchText.value) {
      getDataDebounce('')
    }
  })
  const itemDelete = selectedItems.value.find((i: any) => i[keyLabel.value] === lb)
  if (itemDelete && multiple.value) {
    if (confirmChange.value) {
      if (!confirmed.value) {
        hide()
        confirmed.value = true
        fnConfirm.value = () => handleSelect(itemDelete)
        return
      }
    }
    handleSelect(itemDelete)
  }
}

const prepareComponent = () => {
  if (modelValue) {
    cTempValue.value = modelValue.value
    if (items.value) {
      if (labelOnlyShowText.value) {
        cItems.value = [...items.value] as []
      } else {
        cItems.value = $searchObjects(items.value, keyLabel.value, searchText.value || '', { i: true, limit: limitItems.value }) as []
      }
      if (multiple.value) {
        if (typeof query?.value !== 'function' && !selectedMultiple.value?.length) {
          selectedItems.value = items.value.filter((i: any) => (Array.isArray(modelValue.value) ? modelValue.value.includes(i[keyId.value]) : false)) as (
            | string
            | number
          )[]
        }
      } else {
        selectedItems.value = items.value.filter((i: any) => i[keyId.value] === modelValue.value) as (string | number)[]
        const exists: any = items.value.find((i: any) => i[keyId.value] === modelValue.value)
        if (exists) {
          cLabel.value = exists[keyLabel.value] || text.value || ''
        } else {
          cLabel.value = text.value || ''
        }
      }
    }
  }
}

const setValue = (value: any, labelValue: string) => {
  cValue.value = value
  cLabel.value = labelValue
}

const handleCreateClick = (e: any) => {
  hide()
  if (e instanceof MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
  }
  emits('create-new', cLabel.value)
}

const defineInitData = () => {
  if (multiple.value) {
    if (selectedMultiple.value.length) {
      selectedItems.value = selectedMultiple.value.filter((i: any) =>
        Array.isArray(modelValue.value) ? modelValue.value.includes(i[keyId.value]) : false
      ) as (string | number)[]
    }
  } else if (typeof query?.value === 'function') {
    cLabel.value = cLabel.value ? cLabel.value : text.value || ''
  }
}

const handleDataChange = () => {
  if (!selectedItems.value.length && !$utils.isEmpty(modelValue.value)) {
    selectedItems.value = cItems.value.filter((i: any) =>
      Array.isArray(modelValue.value) ? modelValue.value.includes(i[keyId.value]) : i[keyId.value] === modelValue.value
    ) as (string | number)[]
  }
}

onBeforeMount(() => {
  prepareComponent()
})

onMounted(() => {
  if (modelValue !== undefined && modelValue.value !== null && modelValue.value !== '') {
    if (selectedMultiple.value.length) {
      return defineInitData()
    }

    field.value && field.value.setValue(modelValue.value)
    handleDataChange()
  }
})
const clear = () => {
  selectedItems.value = []
  cItems.value = []
  cLabel.value = ''
}
defineExpose({
  show,
  hide,
  setValue,
  prepareComponent,
  getData,
  handleClear,
  clear
})
</script>
